/**
 * @module varianceanalysis/utils/glListUtils
 * @description Utility functions for GL List management - delta tracking, hierarchy enrichment, and order management
 * 
 * Performance Optimizations:
 * - Delta tracking: Only modified records are sent to server
 * - Snapshot-based change detection: O(1) lookups using Map
 * - Efficient hierarchy computation: O(n) with memoization
 * - Minimized reactivity: Batch updates to reduce Vue re-renders
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/** Root parent values that indicate top-level nodes */
export const ROOT_PARENTS = new Set(["primary", "PRIMARY", "NA", null, undefined, ""]);

/** Offset for ungrouped GLs to push them to the end */
export const UNGROUPED_GL_OFFSET = 10000;

/** Maximum days to consider a GL as "new" */
export const NEW_GL_DAYS_THRESHOLD = 60;

// =============================================================================
// DELTA TRACKING
// =============================================================================

/**
 * Creates a snapshot of the current GL state for change detection
 * Uses a Map for O(1) lookups
 * 
 * @param {Array} glList - Array of GL objects
 * @returns {Map} Map of code -> { order, parent, _id }
 */
export function createSnapshot(glList) {
  const snapshot = new Map();
  
  for (const gl of glList) {
    snapshot.set(gl.code, {
      _id: gl._id,
      order: gl.order,
      parent: gl.parent,
      priority: gl.priority || 0,
    });
  }
  
  return snapshot;
}

/**
 * Computes the delta between current state and original snapshot
 * Only returns items that have actually changed
 * 
 * @param {Array} currentList - Current GL list state
 * @param {Map} originalSnapshot - Original state snapshot from createSnapshot()
 * @returns {Array} Array of change objects: { code, order, parent }
 */
export function computeDelta(currentList, originalSnapshot) {
  const changes = [];
  
  for (const gl of currentList) {
    const original = originalSnapshot.get(gl.code);
    
    // Skip if not in original (shouldn't happen, but safety check)
    if (!original) continue;
    
    // Check if order, parent, or priority changed
    const orderChanged = gl.order !== original.order;
    const parentChanged = gl.parent !== original.parent;
    const priorityChanged = (gl.priority || 0) !== (original.priority || 0);
    
    if (orderChanged || parentChanged || priorityChanged) {
      changes.push({
        code: gl.code,
        order: gl.order,
        parent: gl.parent,
        priority: gl.priority || 0,
      });
    }
  }
  
  return changes;
}

/**
 * Checks if there are any pending changes
 * 
 * @param {Array} currentList - Current GL list state
 * @param {Map} originalSnapshot - Original state snapshot
 * @returns {boolean} True if there are pending changes
 */
export function hasPendingChanges(currentList, originalSnapshot) {
  if (!originalSnapshot || originalSnapshot.size === 0) return false;
  
  for (const gl of currentList) {
    const original = originalSnapshot.get(gl.code);
    if (!original) continue;
    
    if (gl.order !== original.order || gl.parent !== original.parent || (gl.priority || 0) !== (original.priority || 0)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Counts the number of pending changes
 * 
 * @param {Array} currentList - Current GL list state
 * @param {Map} originalSnapshot - Original state snapshot
 * @returns {number} Number of pending changes
 */
export function countPendingChanges(currentList, originalSnapshot) {
  if (!originalSnapshot || originalSnapshot.size === 0) return 0;
  
  let count = 0;
  for (const gl of currentList) {
    const original = originalSnapshot.get(gl.code);
    if (!original) continue;
    
    if (gl.order !== original.order || gl.parent !== original.parent || (gl.priority || 0) !== (original.priority || 0)) {
      count++;
    }
  }
  
  return count;
}

// =============================================================================
// ORDER MANAGEMENT
// =============================================================================

/**
 * Recalculates order values for the entire tree using DFS traversal
 * Preserves the original order range (e.g., 10000-19999 for Balance Sheet)
 * 
 * @param {Array} nodes - Array of GL nodes
 * @returns {Array} Same array with updated order values
 */
export function recalculateOrders(nodes) {
  if (!nodes || nodes.length === 0) return nodes;
  
  // Build lookup map
  const nodeMap = new Map();
  for (const node of nodes) {
    nodeMap.set(node.code, node);
  }
  
  // Find root nodes
  const rootNodes = nodes.filter(
    (node) => ROOT_PARENTS.has(node.parent) || !nodeMap.has(node.parent)
  );
  
  // Sort roots by current order (handles fractional orders correctly)
  rootNodes.sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Determine the base order:
  // - For Balance Sheet (orders >= 10000), preserve the range
  // - For P&L (orders < 10000), always start from 1
  // We check the maximum order to detect if this is a Balance Sheet range
  const maxOrder = Math.max(...nodes.map(n => Math.floor(n.order || 0)));
  const isBalanceSheetRange = maxOrder >= 10000;
  
  let baseOrder;
  if (isBalanceSheetRange) {
    // Balance Sheet: find minimum order >= 10000
    const bsOrders = nodes.map(n => Math.floor(n.order || 0)).filter(o => o >= 10000);
    baseOrder = bsOrders.length > 0 ? Math.min(...bsOrders) : 10000;
  } else {
    // P&L: always start from 1
    baseOrder = 1;
  }
  
  // DFS traversal to assign orders, starting from base order
  let currentOrder = baseOrder;
  const visited = new Set(); // Track visited nodes to avoid duplicates
  
  function traverse(node) {
    // Skip if already visited (prevents duplicates)
    if (visited.has(node.code)) return;
    visited.add(node.code);
    
    node.order = currentOrder++;
    
    if (node.children && node.children.length > 0) {
      // Get child nodes and sort by current order
      const childNodes = node.children
        .map((code) => nodeMap.get(code))
        .filter(Boolean)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      for (const child of childNodes) {
        traverse(child);
      }
    }
  }
  
  for (const root of rootNodes) {
    traverse(root);
  }
  
  // Handle any orphaned nodes that weren't reached via DFS
  // (nodes without proper parent linkage)
  for (const node of nodes) {
    if (!visited.has(node.code)) {
      node.order = currentOrder++;
      visited.add(node.code);
    }
  }
  
  return nodes;
}

/**
 * Moves a GL item relative to a target item
 * Updates the order to position it before or after the target
 * 
 * @param {Array} nodes - Array of GL nodes
 * @param {string} sourceCode - Code of the item to move
 * @param {string} targetCode - Code of the target item
 * @param {'before'|'after'} position - Position relative to target
 * @returns {Array} Updated array with new order values
 */
export function moveItemRelative(nodes, sourceCode, targetCode, position = 'before') {
  const nodeMap = new Map();
  for (const node of nodes) {
    nodeMap.set(node.code, node);
  }
  
  const sourceNode = nodeMap.get(sourceCode);
  const targetNode = nodeMap.get(targetCode);
  
  if (!sourceNode || !targetNode) return nodes;
  
  // Calculate new order using fractional positioning
  // This avoids full recalculation for simple moves
  const newOrder = position === 'before'
    ? targetNode.order - 0.5
    : targetNode.order + 0.5;
  
  sourceNode.order = newOrder;
  
  // Recalculate to get clean integer orders
  return recalculateOrders(nodes);
}

/**
 * Changes the parent of a GL item and recalculates orders
 * 
 * @param {Array} nodes - Array of GL nodes
 * @param {string} code - Code of the item to regroup
 * @param {string} newParent - New parent code (use "PRIMARY" or "NA" for root)
 * @returns {Array} Updated array with new parent and order values
 */
export function changeParent(nodes, code, newParent) {
  const nodeMap = new Map();
  for (const node of nodes) {
    nodeMap.set(node.code, node);
  }
  
  const node = nodeMap.get(code);
  if (!node) return nodes;
  
  // Normalize parent value
  const normalizedParent = (newParent === "PRIMARY" || newParent === "primary") 
    ? "primary" 
    : newParent;
  
  // Update parent
  const oldParent = node.parent;
  node.parent = normalizedParent;
  
  // Update old parent's children array
  if (oldParent && !ROOT_PARENTS.has(oldParent)) {
    const oldParentNode = nodeMap.get(oldParent);
    if (oldParentNode && oldParentNode.children) {
      oldParentNode.children = oldParentNode.children.filter((c) => c !== code);
    }
  }
  
  // Update new parent's children array
  if (!ROOT_PARENTS.has(normalizedParent)) {
    const newParentNode = nodeMap.get(normalizedParent);
    if (newParentNode) {
      if (!newParentNode.children) newParentNode.children = [];
      if (!newParentNode.children.includes(code)) {
        newParentNode.children.push(code);
      }
    }
  }
  
  // Recalculate orders
  return recalculateOrders(nodes);
}

// =============================================================================
// HIERARCHY ENRICHMENT
// =============================================================================

/**
 * Enriches nodes with children array and path
 * Optimized O(n) algorithm with memoization
 * 
 * @param {Array} nodes - Array of GL nodes
 * @returns {Array} Enriched nodes with children and path
 */
export function enrichHierarchy(nodes) {
  if (!nodes || nodes.length === 0) return nodes;
  
  const codeMap = new Map();
  
  // Step 1: Index nodes and initialize children (O(n))
  for (const node of nodes) {
    node.children = [];
    codeMap.set(node.code, node);
  }
  
  // Step 2: Populate children arrays (O(n))
  for (const node of nodes) {
    if (!ROOT_PARENTS.has(node.parent)) {
      const parent = codeMap.get(node.parent);
      if (parent) {
        parent.children.push(node.code);
      }
    }
  }
  
  // Step 3: Build paths with memoization (O(n) amortized)
  const pathCache = new Map();
  
  function findPath(nodeCode) {
    if (pathCache.has(nodeCode)) {
      return pathCache.get(nodeCode);
    }
    
    const node = codeMap.get(nodeCode);
    if (!node) return [];
    
    // Root node
    if (ROOT_PARENTS.has(node.parent)) {
      const path = node.parent === "NA" ? ["NA", node.code] : [node.code];
      pathCache.set(nodeCode, path);
      return path;
    }
    
    // Parent not in dataset - treat as root
    if (!codeMap.has(node.parent)) {
      pathCache.set(nodeCode, [node.code]);
      return [node.code];
    }
    
    // Build path iteratively with cycle detection
    const visited = new Set();
    const pathParts = [];
    let current = node;
    
    while (current) {
      if (visited.has(current.code)) break; // Cycle detected
      visited.add(current.code);
      pathParts.unshift(current.code);
      
      if (ROOT_PARENTS.has(current.parent)) {
        if (current.parent === "NA") pathParts.unshift("NA");
        break;
      }
      
      current = codeMap.get(current.parent);
    }
    
    pathCache.set(nodeCode, pathParts);
    return pathParts;
  }
  
  // Step 4: Compute paths for all nodes
  for (const node of nodes) {
    node.path = findPath(node.code);
  }
  
  return nodes;
}

/**
 * Computes subsequent children (all descendants) for each node
 * Uses iterative approach to avoid stack overflow
 * 
 * @param {Array} nodes - Array of GL nodes with children already populated
 * @returns {Array} Same array with subsequent_children added
 */
export function computeSubsequentChildren(nodes) {
  if (!nodes || nodes.length === 0) return nodes;
  
  const codeMap = new Map();
  for (const node of nodes) {
    codeMap.set(node.code, node);
  }
  
  function getDescendants(startCode) {
    const visited = new Set();
    const stack = [startCode];
    const descendants = [];
    
    while (stack.length > 0) {
      const code = stack.pop();
      if (visited.has(code)) continue;
      visited.add(code);
      
      const node = codeMap.get(code);
      if (!node || !node.children || node.children.length === 0) continue;
      
      for (const childCode of node.children) {
        if (!visited.has(childCode)) {
          descendants.push(childCode);
          stack.push(childCode);
        }
      }
    }
    
    return descendants;
  }
  
  for (const node of nodes) {
    node.subsequent_children = getDescendants(node.code);
  }
  
  return nodes;
}

// =============================================================================
// TREE DATA PROCESSING
// =============================================================================

/**
 * Processes raw GL data into a format suitable for tree display
 * Handles ungrouped GLs, computes levels, and marks new items
 * 
 * @param {Array} rawData - Raw GL data from API
 * @returns {{ processedData: Array, expandedIds: Array }} Processed tree data
 */
export function processTreeData(rawData) {
  if (!rawData || rawData.length === 0) {
    return { processedData: [], expandedIds: [] };
  }
  
  const ungroupedGLs = [];
  const ungroupedGLsChildren = [];
  const result = [];
  const expandIds = [];
  const now = new Date();
  
  // Sort once at the start
  const sortedNodes = [...rawData].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  for (const node of sortedNodes) {
    const isRootVisible = ROOT_PARENTS.has(node.parent);
    
    // Handle ungrouped GLs
    if (node.parent === "NA" || node?.path?.includes("NA")) {
      ungroupedGLs.push(node.code);
      ungroupedGLsChildren.push(node.code, ...(node.children || []));
      
      if ((node.order || 0) < UNGROUPED_GL_OFFSET) {
        node.oldOrder = node.order;
        node.order = (node.order || 0) + UNGROUPED_GL_OFFSET;
      }
    }
    
    // Calculate isGlNew
    const createdAt = node.created_at ? new Date(node.created_at) : now;
    const diffDays = (now - createdAt) / (1000 * 60 * 60 * 24);
    const isGlNew = diffDays <= NEW_GL_DAYS_THRESHOLD;
    
    result.push({
      _id: node._id,
      code: node.code,
      name: node.name,
      parent: node.parent,
      order: node.order,
      path: node.path,
      children: node.children,
      subsequent_children: node.subsequent_children,
      reporting_type: node.reporting_type,
      created_at: node.created_at,
      sub_ledger_exists: node.sub_ledger_exists,
      priority: node.priority || 0,
      level: node.path?.length - 1 || 0,
      glnew: isGlNew,
    });
    
    if (isRootVisible) {
      expandIds.push(node.code);
    }
  }
  
  // Add ungrouped GLs virtual node
  if (ungroupedGLs.length > 0) {
    const NANode = {
      _id: "NA",
      name: "Ungrouped: Other sub-schedules and further Analysis Accounts",
      code: "NA",
      parent: "primary",
      children: ungroupedGLs,
      subsequent_children: ungroupedGLsChildren,
      order: UNGROUPED_GL_OFFSET,
      reporting_type: null,
      level: 0,
      glnew: false,
    };
    result.push(NANode);
    expandIds.push("NA");
  }
  
  // Sort final result by order
  result.sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return { processedData: result, expandedIds: expandIds };
}

// =============================================================================
// SIBLINGS & FILTERING
// =============================================================================

/**
 * Gets sibling GLs (same parent) for move operations
 * Cached for performance
 * 
 * @param {Array} nodes - Array of GL nodes
 * @param {Object} item - Current item
 * @param {Array} allowedTypes - Array of allowed reporting_type values
 * @returns {Array} Array of sibling GLs sorted by order
 */
export function getSiblings(nodes, item, allowedTypes = []) {
  if (!item) return [];
  
  // Default to same type if no filter specified
  const types = allowedTypes.length > 0 ? allowedTypes : [item.reporting_type];
  
  return nodes
    .filter(
      (node) =>
        node.parent === item.parent &&
        types.includes(node.reporting_type) &&
        node.code !== item.code
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Creates a siblings cache for efficient lookups
 * 
 * @param {Array} nodes - Array of GL nodes
 * @returns {Map} Map of cacheKey -> siblings array
 */
export function createSiblingsCache(nodes) {
  const cache = new Map();
  
  // Group by parent
  const parentGroups = new Map();
  for (const node of nodes) {
    const parent = node.parent || "primary";
    if (!parentGroups.has(parent)) {
      parentGroups.set(parent, []);
    }
    parentGroups.get(parent).push(node);
  }
  
  // Sort each group by order
  for (const [parent, siblings] of parentGroups) {
    siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
    cache.set(parent, siblings);
  }
  
  return cache;
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  // Constants
  ROOT_PARENTS,
  UNGROUPED_GL_OFFSET,
  NEW_GL_DAYS_THRESHOLD,
  
  // Delta tracking
  createSnapshot,
  computeDelta,
  hasPendingChanges,
  countPendingChanges,
  
  // Order management
  recalculateOrders,
  moveItemRelative,
  changeParent,
  
  // Hierarchy
  enrichHierarchy,
  computeSubsequentChildren,
  
  // Tree processing
  processTreeData,
  
  // Siblings
  getSiblings,
  createSiblingsCache,
};
